// src/lib/services/authService.ts

import { supabase } from '../../supabaseClient';
import { wellnessService } from '../features/wellnessService';
import type { User as SupabaseUser, Session, AuthChangeEvent } from '@supabase/supabase-js';

// === 2. AUTH & PROFILE MANAGEMENT ===

interface ProfileInput {
  username?: string;
  real_name?: string;
  age?: number;
  interests?: string;
  display_name?: string;
  avatar_url?: string;
  bio?: string;
  is_public?: boolean;
  journey_focus?: string;
}

async function migrateWellnessDataSilently(userId: string | null = null): Promise<void> {
  try {
    const hasWeightData = localStorage.getItem('weightEntries');
    const hasWaterData = localStorage.getItem('weeklyWaterData');
    const hasGoalData = localStorage.getItem('goalWeight');
    const hasSettings = localStorage.getItem('waterGoal') ?? localStorage.getItem('glassSize');

    if (hasWeightData || hasWaterData || hasGoalData || hasSettings) {
      if (userId) {
        await wellnessService.migrateLocalStorageDataForUser(userId);
      } else {
        await wellnessService.migrateLocalStorageData();
      }
    }
  } catch (error) {
    console.warn('⚠️ Wellness data migration failed (non-critical):', error);
  }
}

async function createProfile(user: SupabaseUser, profile: ProfileInput = {}): Promise<void> {
  const defaultDisplay = user.email?.split('@')[0] ?? 'User';
  const { error } = await supabase.from('profiles').insert([
    {
      id: user.id,
      username: profile.username ?? defaultDisplay,
      real_name: profile.real_name ?? null,
      age: profile.age ?? null,
      interests: profile.interests ?? '',
      display_name: profile.display_name ?? defaultDisplay,
      avatar_url: profile.avatar_url ?? null,
      bio: profile.bio ?? '',
      is_public: profile.is_public === true,
      journey_focus: profile.journey_focus ?? '',
    },
  ]);
  if (error) throw error;
  await migrateWellnessDataSilently(user.id);
}

interface AuthResponse {
  user: SupabaseUser | null;
  session: Session | null;
}

interface AuthService {
  signUp(email: string, password: string, profile?: ProfileInput): Promise<AuthResponse>;
  signIn(email: string, password: string): Promise<AuthResponse>;
  signOut(): Promise<void>;
  getCurrentUser(): Promise<SupabaseUser | null>;
  resetPassword(email: string): Promise<{ error?: Error | null }>;
  updatePassword(newPassword: string): Promise<{ error?: Error | null }>;
  deleteAccount(): Promise<{ error?: Error | null }>;
  onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void): () => void;
}

const authService: AuthService = {
  async signUp(email: string, password: string, profile: ProfileInput = {}): Promise<AuthResponse> {
    // Extract ai_consent_at from profile input and store in Supabase user metadata
    const { ai_consent_at, ...profileFields } = profile as ProfileInput & { ai_consent_at?: string };
    const signUpOptions = ai_consent_at ? { data: { ai_consent_at } } : undefined;
    const { data, error } = await supabase.auth.signUp({ email, password, options: signUpOptions });
    if (error) throw error;

    // Try to create profile, but don't fail signup if it doesn't work
    if (data?.user) {
      try {
        await createProfile(data.user, profileFields);
      } catch (profileError) {
        console.warn('Profile creation failed, but signup succeeded:', profileError);
        // Profile will be created on first signin instead
      }
    }
    return data;
  },
  async signIn(email: string, password: string): Promise<AuthResponse> {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;

    if (data?.user) {
      // Check if profile exists, create if missing
      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('id')
          .eq('id', data.user.id)
          .maybeSingle();

        if (!profile) {
          // Profile doesn't exist, create it now
          await createProfile(data.user, {});
        }
      } catch (profileError) {
        console.warn('Could not check/create profile:', profileError);
      }

      await migrateWellnessDataSilently();
    }
    return data;
  },
  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  },
  async getCurrentUser(): Promise<SupabaseUser | null> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return user;
  },
  async resetPassword(email: string): Promise<{ error?: Error | null }> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}?reset=true`,
    });
    if (error) throw error;
    return { error: null };
  },
  async updatePassword(newPassword: string): Promise<{ error?: Error | null }> {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
    return { error: null };
  },
  async deleteAccount(): Promise<{ error?: Error | null }> {
    // This deletes the current user's own account
    const { error } = await supabase.rpc('admin_delete_user_completely' as never);
    if (error) throw error;
    return { error: null };
  },
  onAuthStateChange(callback: (event: AuthChangeEvent, session: Session | null) => void): () => void {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(callback);
    return () => subscription.unsubscribe();
  },
};

export { authService, createProfile };
export type { ProfileInput };
